import React, { useMemo, useState, useEffect } from "react";
import { Modal, Button, Form, Spinner } from "react-bootstrap";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";

const BulkInvoiceModal = ({
  open,
  onClose,
  vendors = [],
  selectedVendors,
  setSelectedVendors,
  startDate,
  setStartDate,
  endDate,
  setEndDate,
  onDownload,
  loading
}) => {
  const [activeTab, setActiveTab] = useState("Date");
  const [vendorSearch, setVendorSearch] = useState("");

  // ✅ Get userType directly here
  const userType = JSON.parse(localStorage.getItem("user"))?.userType;

  /* -------------------- AUTO-LOCK VENDOR -------------------- */
  // If Vendor user, auto-select their vendor (vendors array should already be filtered)
  useEffect(() => {
    if (userType === "Vendor" && vendors.length === 1) {
      setSelectedVendors(vendors);
    }
  }, [userType, vendors, setSelectedVendors]);

  /* -------------------- VENDORS (ADMIN ONLY) -------------------- */

  const filteredVendors = useMemo(() => {
    if (userType !== "ADMIN") return [];

    return vendors.filter(
      v =>
        v &&
        typeof v.label === "string" &&
        v.label.toLowerCase().includes(vendorSearch.toLowerCase())
    );
  }, [vendors, vendorSearch, userType]);

  const allSelected =
    userType === "ADMIN" &&
    vendors.length > 0 &&
    selectedVendors.length === vendors.length;

  const toggleVendor = (vendor) => {
    if (userType !== "ADMIN") return;

    const exists = selectedVendors.some(v => v.value === vendor.value);

    if (exists) {
      setSelectedVendors(selectedVendors.filter(v => v.value !== vendor.value));
    } else {
      setSelectedVendors([...selectedVendors, vendor]);
    }
  };

  const toggleSelectAll = () => {
    if (userType !== "ADMIN") return;

    if (allSelected) {
      setSelectedVendors([]);
    } else {
      setSelectedVendors(vendors);
    }
  };

  /* -------------------- DATE -------------------- */

  const handleMonthSelect = (value) => {
    if (!value) return;

    const start = dayjs(value).startOf("month");
    const end = dayjs(value).endOf("month");

    setStartDate(start);
    setEndDate(end);
  };

  return (
    <Modal
      show={open}
      onHide={onClose}
      centered
      dialogClassName="bulk-invoice-modal"
    >
      {/* HEADER */}
      <Modal.Header
        closeButton
        style={{ padding: "20px 24px", borderBottom: "1px solid #e5e7eb" }}
      >
        <Modal.Title style={{ fontSize: 18, fontWeight: 600 }}>
          Bulk Invoice Download
        </Modal.Title>
      </Modal.Header>

      {/* BODY */}
      <Modal.Body className="p-0">
        <div className="d-flex" style={{ minHeight: 380 }}>
          {/* LEFT PANEL */}
          <div className="border-end bg-light" style={{ width: 200 }}>
            {[
              "Date",
              ...(userType === "ADMIN" ? ["Vendors"] : [])
            ].map(tab => (
              <div
                key={tab}
                className={`p-3 border-bottom ${
                  activeTab === tab ? "bg-white fw-bold" : ""
                }`}
                style={{ cursor: "pointer", fontSize: 14 }}
                onClick={() => setActiveTab(tab)}
              >
                {tab}
                {tab === "Vendors" && selectedVendors.length > 0 && (
                  <span className="ms-1">({selectedVendors.length})</span>
                )}
              </div>
            ))}
          </div>

          {/* RIGHT PANEL */}
          <div className="p-3" style={{ flex: 1 }}>
            {/* DATE TAB */}
            {activeTab === "Date" && (
              <>
                <h6 className="fw-bold mb-3">Select Month</h6>

                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    value={startDate}
                    onChange={handleMonthSelect}
                    views={["year", "month"]}
                    format="MM/YYYY"
                    maxDate={dayjs()}
                    slotProps={{
                      textField: {
                        size: "small",
                        fullWidth: true,
                        placeholder: "MM/YYYY"
                      }
                    }}
                  />
                </LocalizationProvider>

                <hr />

                <Form.Group className="mb-3">
                  <Form.Label>From</Form.Label>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker
                      value={startDate}
                      onChange={setStartDate}
                      format="DD/MM/YYYY"
                      maxDate={dayjs()}
                      slotProps={{
                        textField: { size: "small", fullWidth: true }
                      }}
                    />
                  </LocalizationProvider>
                </Form.Group>

                <Form.Group>
                  <Form.Label>To</Form.Label>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker
                      value={endDate}
                      onChange={setEndDate}
                      minDate={startDate}
                      maxDate={dayjs()}
                      format="DD/MM/YYYY"
                      slotProps={{
                        textField: { size: "small", fullWidth: true }
                      }}
                    />
                  </LocalizationProvider>
                </Form.Group>
              </>
            )}

            {/* VENDORS TAB (ADMIN ONLY) */}
            {userType === "ADMIN" && activeTab === "Vendors" && (
              <>
                <div className="d-flex align-items-center justify-content-between mb-2">
                  <h6 className="fw-bold mb-0">Select Vendors</h6>

                  <Form.Check
                    type="checkbox"
                    checked={allSelected}
                    onChange={toggleSelectAll}
                  />
                </div>

                <Form.Control
                  type="text"
                  placeholder="Search vendors..."
                  className="mb-3"
                  size="sm"
                  value={vendorSearch}
                  onChange={(e) => setVendorSearch(e.target.value)}
                />

                <div style={{ maxHeight: 270, overflowY: "auto" }}>
                  {filteredVendors.map(vendor => {
                    const checked = selectedVendors.some(
                      v => v.value === vendor.value
                    );

                    return (
                      <div
                        key={vendor.value}
                        className="d-flex align-items-center p-2 mb-1"
                        style={{
                          cursor: "pointer",
                          borderRadius: 6,
                          fontSize: 14,
                          background: checked ? "#eef2f7" : "transparent"
                        }}
                        onClick={() => toggleVendor(vendor)}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          readOnly
                          style={{ marginRight: 10 }}
                        />
                        <span>{vendor.label}</span>
                      </div>
                    );
                  })}

                  {filteredVendors.length === 0 && (
                    <div className="text-center text-muted py-3">
                      No vendors found
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </Modal.Body>

      {/* FOOTER */}
      <Modal.Footer
        className="d-flex justify-content-between"
        style={{ padding: "12px 20px", borderTop: "1px solid #e5e7eb" }}
      >
        <Button
          variant="link"
          className="text-dark p-0"
          onClick={() => {
            if (userType === "ADMIN") {
              setSelectedVendors([]);
            }
            setStartDate(null);
            setEndDate(null);
          }}
        >
          Clear all
        </Button>

        <Button
          className="request_btn"
          disabled={loading}
          onClick={onDownload}
          style={{ minWidth: 160 }}
        >
          {loading ? (
            <>
              <Spinner size="sm" className="me-2" />
              Downloading
            </>
          ) : (
            "Download invoices"
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default BulkInvoiceModal;
